/**
 * P-Process — đơn đang ở công đoạn nào.
 *
 * Danh sách bước lấy nguyên từ tờ "P 流程" khách đưa ngày 13/09/2026. KHÔNG bịa
 * thêm bước nào: nhìn thấy một bước trên màn hình là người dùng tin hệ thống
 * đang theo dõi bước đó.
 *
 * Để cả chuỗi ở dạng KHAI BÁO chứ không rải vào code, vì khách đã nói trước là
 * sẽ còn đổi. Thêm bớt bước, đổi thứ tự, đổi chứng từ nào tính là xong — đều
 * sửa đúng mảng dưới đây, và bộ kiểm bên cạnh giữ cho không sai.
 *
 * Trước đây P-Process chỉ là một ô bật/tắt (`proforma_invoices.p_process`).
 * Ô đó GIỮ NGUYÊN ý nghĩa "đơn này chạy theo quy trình P"; tệp này trả lời câu
 * hỏi tiếp theo — chạy tới đâu rồi.
 */

/* ─────────────────────────────  Điều kiện áp dụng  ────────────────────── */

/**
 * Bước này có áp dụng cho đơn đang xét không.
 *
 * `purchased`  — chỉ khi phải đi mua nguyên liệu (nhánh "không có NVL").
 * `subcontracted` — chỉ khi có gia công ngoài.
 *
 * Bước không áp dụng KHÔNG được tính là thiếu sót. Đơn có sẵn nguyên liệu mà
 * hiện "chưa mua hàng" màu đỏ thì người dùng sẽ đi tìm một việc không tồn tại.
 */
export type StepCondition = "always" | "purchased" | "subcontracted" | "produced";

export interface PProcessStep {
  code: string;
  /** Thứ tự trên tờ giấy. */
  seq: number;
  /** Báo cáo kiểm thứ mấy (tờ giấy đánh số 1..5), nếu bước này là một báo cáo. */
  testNo?: 1 | 2 | 3 | 4 | 5;
  applies: StepCondition;
  /**
   * Chứng từ nào trong hệ chứng minh bước này đã xong.
   * `manual` = hệ chưa có chỗ ghi, nhân viên tự đánh dấu kèm tệp scan.
   */
  evidence: "ledger" | "manual";
  /**
   * Các bước cùng một nhóm chạy SONG SONG, không nối tiếp.
   * Dùng cho hai đường đóng gói cuối chuỗi.
   */
  group?: string;
  /**
   * Bước TÙY CHỌN: có chứng từ thì tính là xong, không có thì bỏ qua — không chặn chuỗi, không tính vào tổng số nấc.
   * Chỉ quy trình thường dùng (đơn bán hàng, phiếu đóng gói — anh Toàn duyệt 29/10/2026); tờ "P 流程" không có bước nào tùy chọn.
   */
  optional?: boolean;
  /**
   * Làm TRƯỚC các bước đứng trước nó là chuyện bình thường, không tính là nhảy cóc — khách trả trước khi giao hàng.
   * Thứ tự trên màn hình giữ nguyên; chỉ không gắn nhãn "Làm nhảy cóc" cho bước này.
   */
  anyOrder?: boolean;
  /** Tờ giấy ghi rõ chứng từ này phải mang số nào. */
  carries: readonly ("lot" | "pi")[];
}

/**
 * Nhóm song song xong khi nào.
 *
 * `any` — có một đường là đủ. Khách hiện dùng một trong hai đường đóng gói
 * (đóng gói thường HOẶC sàng máy chọn quang), và tờ giấy ghi tay xác nhận
 * đường máy chọn quang cũng xuất chứng từ. Nếu mai kia bắt buộc đủ cả hai thì
 * đổi đúng chữ này thành `all`.
 */
export const GROUP_RULES: Record<string, "any" | "all"> = { packing: "any" };

/** 13 bước, đúng thứ tự tờ "P 流程". */
export const P_PROCESS_STEPS: readonly PProcessStep[] = [
  // Khách đặt PO cho ta — chứng từ bắt buộc là PI.
  { code: "piIssued", seq: 1, applies: "always", evidence: "ledger", carries: ["pi"] },
  // Kiểm còn nguyên liệu không → rẽ hai nhánh.
  // Tờ giấy vẽ ô này là MỘT ô rẽ nhánh, nên giữ nguyên một nấc: đơn có sẵn
  // nguyên liệu chỉ cần bước kiểm, đơn phải mua thì nấc này gồm cả đơn mua.
  { code: "materialCheck", seq: 2, applies: "always", evidence: "ledger", carries: [] },
  // Nhánh "không có NVL": đơn mua, ghi rõ khi giao phải kèm bản sao đơn mua,
  // bản sao chứng nhận chất lượng và phiếu giao gốc có ghi số LOT.
  { code: "purchaseOrder", seq: 2, applies: "purchased", evidence: "ledger", carries: ["lot"] },
  { code: "incomingTest", seq: 3, testNo: 1, applies: "purchased", evidence: "ledger", carries: ["lot"] },
  { code: "productionOrder", seq: 4, applies: "always", evidence: "ledger", carries: ["lot"] },
  { code: "materialLedger", seq: 5, applies: "always", evidence: "ledger", carries: ["lot"] },
  { code: "materialIssue", seq: 6, applies: "always", evidence: "ledger", carries: ["lot"] },
  { code: "ipqc", seq: 7, testNo: 2, applies: "always", evidence: "ledger", carries: ["lot", "pi"] },
  // Phiếu kiểm nút lần 1 — ngoại quan / kích thước / màu / lực đóng mở / lực phá hủy.
  { code: "buttonTest1", seq: 8, testNo: 3, applies: "always", evidence: "ledger", carries: ["lot", "pi"] },
  { code: "subcontractOrder", seq: 9, applies: "subcontracted", evidence: "ledger", carries: ["lot", "pi"] },
  { code: "subcontractMoves", seq: 10, applies: "subcontracted", evidence: "ledger", carries: ["lot", "pi"] },
  // Máy dò kim — báo cáo kiểm #4, ghi thẳng vào sổ từ 15/09/2026.
  //
  // Khách xác nhận 14/09/2026 ba điều còn thiếu: kiểm 100% (không lấy mẫu),
  // ngưỡng 0, và người ký là quản trị. Trước đó đây là ô đánh dấu tay kèm tệp
  // scan — hệ không giữ một con số nào, nên không ai trả lời được lô đó máy
  // loại ra bao nhiêu cái.
  { code: "needleTest", seq: 11, testNo: 4, applies: "always", evidence: "ledger", carries: ["lot", "pi"] },
  // Phiếu kiểm nút lần 2 — sau gia công ngoài, nên chỉ áp dụng khi có gia công.
  { code: "buttonTest2", seq: 12, testNo: 5, applies: "subcontracted", evidence: "ledger", carries: ["lot", "pi"] },
  // Hai đường đóng gói chạy song song, không nối tiếp nhau.
  { code: "packingRecord", seq: 13, applies: "always", evidence: "ledger", group: "packing", carries: ["lot", "pi"] },
  // Sàng máy chọn quang: chạy 100% qua máy, ghi số vào / số máy loại / tỉ lệ
  // loại. Cùng sổ báo cáo kiểm với máy dò kim.
  { code: "opticalPacking", seq: 13, applies: "always", evidence: "ledger", group: "packing", carries: ["lot", "pi"] },
];

export const P_PROCESS_STEP_CODES: readonly string[] = P_PROCESS_STEPS.map((s) => s.code);

/**
 * Chỗ gắn ảnh trên Quy trình P (Roary 04/11/2026): mỗi nấc một ô, cộng `general` cho ảnh không thuộc nấc nào. Nấc "Khách đặt hàng"
 * không có ô riêng — ảnh đơn đặt hàng đã lưu trên PI (bằng chứng đặt hàng) và được hiện lại ở nấc đó, không lưu hai lần.
 */
export const P_PROCESS_PHOTO_SLOTS: readonly string[] = [...P_PROCESS_STEP_CODES.filter((c) => c !== "piIssued"), "general"];

/* ─────────────────────────────  Tính trạng thái  ──────────────────────── */

/** Bằng chứng đọc được từ sổ, cho MỘT lệnh sản xuất (hoặc một PI chưa có lệnh). */
export interface PProcessEvidence {
  /** Đơn phải đi mua nguyên liệu — quyết định nhánh trên cùng. */
  purchased: boolean;
  /** Đơn có gia công ngoài — quyết định ba bước 10, 11 và phiếu kiểm nút lần 2. */
  subcontracted: boolean;
  /** Đơn có lệnh sản xuất — chỉ quy trình thường dùng (`orderProcess.ts`); quy trình P luôn sản xuất. */
  produced?: boolean;
  /** Mã bước đã có chứng từ trong sổ hoặc đã được đánh dấu tay. */
  done: readonly string[];
  /**
   * Mã bước KIỂM có kết luận TRƯỢT.
   *
   * Trước 15/09/2026 chỗ này không tồn tại: màn hình chỉ hỏi "có bản ghi kiểm
   * không". Một lô TRƯỢT vì thế hiện y hệt một lô ĐẠT — xanh, đã xong. Đó là
   * kiểu sai nguy hiểm nhất ở một màn hình theo dõi: nó không im lặng, nó nói
   * ngược lại sự thật, và người quản lý đọc xong yên tâm cho hàng đi tiếp.
   */
  failed?: readonly string[];
  /**
   * Mã bước TRƯỢT nhưng đã được CHO QUA CÓ PHÊ DUYỆT qua một NCR đã đóng.
   *
   * Khách trả lời 15/09/2026: người cho qua là quản trị. Cần trạng thái riêng
   * vì hai cách hiển thị sai đều tệ như nhau — để nguyên đỏ thì màn hình nói
   * đơn đang kẹt trong khi hàng đã giao xong; chuyển sang xanh thì mất dấu vết
   * là lô này từng trượt và có người phải chịu trách nhiệm cho nó đi tiếp.
   */
  conceded?: readonly string[];
}

/** `optional` = bước tùy chọn chưa có chứng từ — đã bỏ qua, không phải việc còn treo. */
export type StepState = "done" | "pending" | "notApplicable" | "failed" | "concession" | "optional";

/** Bước không tính vào nấc: không áp dụng cho đơn, hoặc tùy chọn mà không lập. */
export function stepSkipped(state: StepState): boolean {
  return state === "notApplicable" || state === "optional";
}

export interface PProcessStepState {
  code: string;
  seq: number;
  testNo?: 1 | 2 | 3 | 4 | 5;
  evidence: "ledger" | "manual";
  group?: string;
  state: StepState;
}

export function stepApplies(step: PProcessStep, e: Pick<PProcessEvidence, "purchased" | "subcontracted" | "produced">): boolean {
  if (step.applies === "purchased") return e.purchased;
  if (step.applies === "subcontracted") return e.subcontracted;
  if (step.applies === "produced") return !!e.produced;
  return true;
}

/** Trạng thái của từng bước, giữ nguyên thứ tự tờ giấy. */
export function pProcessSteps(e: PProcessEvidence): PProcessStepState[] {
  return processSteps(P_PROCESS_STEPS, e);
}

/**
 * Như `pProcessSteps` nhưng cho một chuỗi bước bất kỳ — quy trình thường (`orderProcess.ts`, 29/10/2026) dùng CÙNG cách tính, chỉ
 * khác danh sách bước. Một bộ giải cho hai màn: đổi luật (trượt thắng xong, nấc không áp dụng coi như qua) là đổi cho cả hai.
 */
export function processSteps(stepsDef: readonly PProcessStep[], e: PProcessEvidence): PProcessStepState[] {
  const done = new Set(e.done);
  const failed = new Set(e.failed ?? []);
  const conceded = new Set(e.conceded ?? []);
  return stepsDef.map((step) => ({
    code: step.code,
    seq: step.seq,
    ...(step.testNo ? { testNo: step.testNo } : {}),
    evidence: step.evidence,
    ...(step.group ? { group: step.group } : {}),
    /**
     * TRƯỢT thắng ĐÃ XONG. Một lô có bản ghi kiểm kết luận trượt vẫn thỏa điều
     * kiện "đã có chứng từ", nên nếu xét `done` trước thì nó lại hiện xanh —
     * đúng cái lỗi đang sửa.
     */
    state: !stepApplies(step, e)
      ? "notApplicable"
      : failed.has(step.code)
        ? conceded.has(step.code)
          ? "concession"
          : "failed"
        : done.has(step.code)
          ? "done"
          : step.optional
            ? "optional"
            : "pending",
  }));
}

/**
 * Một nấc (seq) đã xong chưa.
 *
 * Nấc chỉ có các bước "không áp dụng" thì coi như đã qua — nếu không, đơn có
 * sẵn nguyên liệu sẽ đứng mãi ở bước mua hàng. Bước tùy chọn không lập cũng vậy.
 */
function seqDone(states: PProcessStepState[], seq: number): boolean {
  const at = states.filter((s) => s.seq === seq);
  const live = at.filter((s) => !stepSkipped(s.state));
  if (!live.length) return true;
  // Một bước TRƯỢT chặn cả nấc, kể cả nhóm "có một đường là xong": đường kia
  // xong không xóa được việc lô này đã trượt ở đường này.
  //
  // "Cho qua có phê duyệt" thì KHÔNG chặn: lô đó đã được quản trị cho đi tiếp
  // và hàng thật sự đi tiếp. Giữ nấc đỏ mãi là màn hình nói đơn đang kẹt trong
  // khi nó đã giao xong — sai theo chiều ngược lại, nhưng vẫn là sai. Dấu vết
  // nằm ở trạng thái `concession` của chính bước đó, không mất đi đâu.
  if (live.some((s) => s.state === "failed")) return false;
  const group = live[0]?.group;
  const passed = (s: PProcessStepState) => s.state === "done" || s.state === "concession";
  if (group && GROUP_RULES[group] === "any") return live.some(passed);
  return live.every(passed);
}

export interface PProcessProgress {
  steps: PProcessStepState[];
  /** Bước đang chờ làm tiếp. `null` = đã xong cả chuỗi. */
  currentStep: string | null;
  /** Đã qua bao nhiêu nấc trên tổng số nấc CÓ ÁP DỤNG. */
  doneCount: number;
  totalCount: number;
  complete: boolean;
  /**
   * Bước đã xong nhưng nằm SAU một bước còn dang dở.
   *
   * Xưởng làm nhảy cóc là chuyện có thật, và đó chính là thứ người quản lý cần
   * nhìn thấy — chứ không phải bị thanh tiến độ làm cho tưởng mọi thứ đúng thứ tự.
   */
  outOfOrder: readonly string[];
  /** Bước kiểm đã kết luận TRƯỢT — chuỗi dừng ở đây cho tới khi xử lý xong. */
  failed: readonly string[];
  /** Bước đã trượt nhưng được cho qua có phê duyệt — chuỗi đi tiếp, dấu vết ở lại. */
  conceded: readonly string[];
}

export function pProcessProgress(e: PProcessEvidence): PProcessProgress {
  return processProgress(P_PROCESS_STEPS, e);
}

/** Như `pProcessProgress` nhưng cho một chuỗi bước bất kỳ (`processSteps`). */
export function processProgress(stepsDef: readonly PProcessStep[], e: PProcessEvidence): PProcessProgress {
  const steps = processSteps(stepsDef, e);
  const seqs = [...new Set(steps.map((s) => s.seq))].sort((a, b) => a - b);
  const applicable = seqs.filter((seq) => steps.some((s) => s.seq === seq && !stepSkipped(s.state)));
  const anyOrder = new Set(stepsDef.filter((s) => s.anyOrder).map((s) => s.code));

  const doneSeqs = applicable.filter((seq) => seqDone(steps, seq));
  const firstOpen = applicable.find((seq) => !seqDone(steps, seq));
  // Nấc đang dở vì có bước TRƯỢT thì chỉ vào chính bước trượt, không chỉ vào
  // bước còn chờ bên cạnh — thứ cần xử lý là cái đã trượt.
  const current = firstOpen === undefined
    ? null
    : steps.find((s) => s.seq === firstOpen && s.state === "failed")?.code
      ?? steps.find((s) => s.seq === firstOpen && s.state === "pending")?.code
      ?? null;

  // Bước "cho qua có phê duyệt" cũng tính là đã qua khi xét nhảy cóc: nó nằm
  // sau lưng chuỗi rồi, không phải việc còn treo. Bước `anyOrder` (thu tiền trước khi giao) không tính.
  const outOfOrder = firstOpen === undefined
    ? []
    : steps
        .filter((s) => s.seq > firstOpen && (s.state === "done" || s.state === "concession") && !anyOrder.has(s.code))
        .map((s) => s.code);

  return {
    steps,
    currentStep: current,
    doneCount: doneSeqs.length,
    totalCount: applicable.length,
    complete: firstOpen === undefined,
    outOfOrder,
    failed: steps.filter((s) => s.state === "failed").map((s) => s.code),
    conceded: steps.filter((s) => s.state === "concession").map((s) => s.code),
  };
}

/** Một dòng trên màn hình theo dõi. */
export interface PProcessRow extends PProcessProgress {
  piId: string;
  piNo: string;
  customerName: string | null;
  /** Lệnh sản xuất của dòng này; rỗng khi PI chưa mở lệnh nào. */
  moId: string | null;
  moNo: string | null;
  lotNumbers: readonly string[];
  purchased: boolean;
  subcontracted: boolean;
  /** Số ảnh / tệp đã gắn trên Quy trình P của PI này (mọi nấc, không gồm bằng chứng đặt hàng trên PI). */
  photoCount: number;
}
